/* eslint-disable no-underscore-dangle */
import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import { selectCurrentRoom } from '@store/actionSlices/appConfig'
import { RouteKey } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

import {
  FULFILLED_STATUS,
  REJECTED_STATUS,
} from '@components/data-handler/data-handler'
import MENU_ITEMS from '@components/navigation/menu-items'

import {
  selectFromResult as selectProjectConfigFromResult,
  useGetConfigQuery,
} from '@api/config'
import {
  selectShowcaseRoomConfigFromResult,
  useGetShowcaseRoomConfigQuery,
} from '@api/showcase-room'

import { hasToken } from '@utilities/helper'

type Template = 'default' | 'v2'

interface IdleRoute {
  key: string
  template?: Template
  ignoreIdlePageActiveStatus?: boolean
}

const IDLE_ROUTES: IdleRoute[] = [
  {
    key: RouteKey.AREA_VIEW,
  },
  {
    key: RouteKey.LEVEL_VIEW,
  },
  {
    key: RouteKey.AREA_VIEW_HOUSE_AND_LAND,
  },
  {
    key: RouteKey.PRECINCT,
  },
  {
    key: RouteKey.EXTERNAL_VIEWS,
    template: 'v2',
    ignoreIdlePageActiveStatus: true,
  },
]

const FALLBACK_ROUTE = RouteKey.VISION

const useIdlePage = () => {
  const token = useSelector((state: RootStateTypeExtra) => state.token)
  const disable = React.useMemo(() => !hasToken(token), [token])
  const history = useHistory()
  const location = useLocation()
  const projectName = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity?.projectName
  )
  const connected = useSelector(
    (state: RootStateTypeExtra) => state.firestore?.session?.connected
  )
  const activeRoute = useSelector(
    (state: RootStateTypeExtra) => state.firestore?.session?.activeRoute
  )
  const currentRoom = useSelector(selectCurrentRoom)

  const projectConfigResponse = useGetConfigQuery(
    {
      projectName,
    },
    {
      skip: disable,
      selectFromResult: selectProjectConfigFromResult,
    }
  )

  const idlePageConfig = projectConfigResponse?.configInfo?.idlePage
  const idleRoute = React.useMemo(
    () => IDLE_ROUTES.find((route) => route.key === activeRoute),
    [activeRoute]
  )

  const showcaseRoomConfigResponse = useGetShowcaseRoomConfigQuery(
    {
      projectName,
      showcaseRoomId: currentRoom,
    },
    {
      skip: disable || !currentRoom,
      selectFromResult: selectShowcaseRoomConfigFromResult,
    }
  )

  const { navigationSettings, templates: templateSettings } =
    showcaseRoomConfigResponse?.showcaseRoomConfig || {}

  const isLoaded = React.useMemo<boolean>(() => {
    if (
      !(
        projectConfigResponse?.status === FULFILLED_STATUS ||
        projectConfigResponse?.status === REJECTED_STATUS
      )
    ) {
      return false
    }
    if (
      !(
        showcaseRoomConfigResponse?.status === FULFILLED_STATUS ||
        showcaseRoomConfigResponse?.status === REJECTED_STATUS
      )
    ) {
      return false
    }
    return true
  }, [projectConfigResponse?.status, showcaseRoomConfigResponse?.status])

  const idlePageTemplate = React.useMemo<string | undefined>(() => {
    if (activeRoute && templateSettings) {
      return templateSettings?.[activeRoute]?.template
    }
    return undefined
  }, [activeRoute, templateSettings])

  const canRedirectToIdlePage = React.useMemo<boolean>(() => {
    if (!connected || !idleRoute || !isLoaded) {
      return false
    }

    if (!idleRoute.ignoreIdlePageActiveStatus && !idlePageConfig?.active) {
      return false
    }

    if (!idleRoute.template) {
      return true
    }

    if (idleRoute.template === idlePageTemplate) {
      return true
    }

    return false
  }, [connected, idleRoute, isLoaded, idlePageConfig?.active, idlePageTemplate])

  const isIdlePageNavigable = React.useMemo<boolean>(() => {
    if (disable) {
      return false
    }
    if (
      showcaseRoomConfigResponse.status === FULFILLED_STATUS &&
      !navigationSettings?.length
    ) {
      return true
    }
    const matchedMenuItem = idlePageConfig?.page
      ? MENU_ITEMS.find(
          (item) =>
            item.route === idlePageConfig.page ||
            item.relatedRoutes?.includes(idlePageConfig.page)
        )
      : undefined
    const matchedNavigationSetting = navigationSettings?.find(
      (item) => item.key === matchedMenuItem?.key
    )
    return !!matchedNavigationSetting?.active
  }, [
    navigationSettings,
    idlePageConfig?.page,
    disable,
    showcaseRoomConfigResponse.status,
  ])

  const redirectionPath = React.useMemo<string>(() => {
    if (isIdlePageNavigable && idlePageConfig?.active && idlePageConfig?.page) {
      return idlePageConfig.page
    }
    return FALLBACK_ROUTE
  }, [idlePageConfig, isIdlePageNavigable])

  const redirectToIdlePage = React.useCallback(() => {
    const currentPath = location.pathname.split('/')?.[2]
    if (currentPath === redirectionPath) {
      return
    }
    history.push({
      pathname: redirectionPath,
      state: { from: location.pathname },
    })
  }, [location.pathname, redirectionPath])

  return {
    canRedirectToIdlePage,
    redirectToIdlePage,
    dataLoaded: isLoaded,
  }
}

export default useIdlePage
